import { TicketsAware } from "@services/account/types";
import { Indexed, ListPage } from "@services/common/types";
import { Device } from "@services/devices/types";
import { LocationEntity } from "@services/locations/types";
import { RoomAccountVisibility } from "@services/meetings/types";
import { WorkflowInstance } from "@services/workflows/types";

export type IndexedRoom = Room & Indexed;
export type IndexedRoomsMeta = Pick<
  MonitoredDeviceListOfMonitoredRoom,
  "size" | "total" | "index"
>;

export interface MonitoredRoom extends Room {
  workflowsInstances?: Array<WorkflowInstance>;
  devices?: Array<Device>;
}

export interface MonitoredDeviceListOfMonitoredRoom extends ListPage<Room> {
  counters?: RoomCounters;
}

export interface Attachment {
  accountId: string;
  contentType: string;
  createdAt: number;
  createdBy: string;
  entityId: string;
  entityType: string;
  fileKind: string;
  fileName: string;
  id: string;
  sizeBytes: number;
}
export interface RoomCounters {
  devicesApiError: number;
  devicesTotal: number;
  devicesUnreachable: number;
  locationsTotal: number;
  roomsManaged: number;
  roomsTotal: number;
  totalFilteredDevices: number;
  devicesError: number;
  devicesMonitoringPaused: number;
  locationsError: number;
  roomsError: number;
}

export interface Room extends TicketsAware {
  logo?: string;
  exchangeId?: string;
  details?: RoomDetails;
  capacity?: number;
  managed?: boolean;
  inService?: boolean;
  logoId?: number;
  hasUnProvisionedDevice?: boolean;
  name?: string;
  id?: string;
  status?: RoomStatus;
  schedulingOption?: SchedulingOption;
  summary?: RoomSummary;
  vip?: boolean;
  location?: LocationEntity;
  devices?: Omit<Device, "room">[];
  numFiles?: number;
  tags?: string[];
}

export interface RoomDetails {
  externalId?: string;
  id?: string;
  publicRoom?: boolean;
  visibleAccounts?: Array<RoomAccountVisibility>;
  contactPhone?: string;
  siteCode?: string;
  watermark?: string;
  banId?: number;
  roomType?: string;
  monitoredIpAddress?: string;
  reCertificationDates?: Array<number>;
  extendedProperties?: RoomExtendedProperties;
  room?: Room;
  contactEmail?: string;
  contactUrl?: string;
  producerNotes?: string;
  serviceExpirationDate?: number;
  createdDate?: number;
  notes?: string;
  customizedPropertyValues?: { [key: string]: string };
  contactName?: string;
  taxId?: string;
  lastUpdatedDate?: number;
  controlPanelUrl?: string;
  roomNumber?: string;
  certificationDetails?: RoomCertificationDetails | null;
  certifiedBy?: string | null;
  certificationStatus?: "Unknown" | "Expired" | "Certified";
  hasRoomController?: boolean;
  visibleAccountIds?: Array<string>;
  certificationDate?: number | null;
  subscriptionId?: string;
}

export interface RoomCertificationDetails {
  Comment?: string;
  comment?: string;
}
export interface RoomExtendedProperties {
  oiTeamEmail?: string;
  oiTeamPhone?: string;
  oiTSSBatLine?: string;
  oiZipCode?: string;
  oiSchedulingRoomContact?: string;
  smName?: string;
  smEmail?: string;
  smPhone?: string;
  smPhoneCell?: string;
  fmName?: string;
  fmEmail?: string;
  fmPhone?: string;
  fmPhoneCell?: string;
  scNonEmergencyPhone?: string;
  scOfficeSecurityPhone?: string;
  ariRoomSerialNumber?: string;
  ariEndOfCoverageDates?: string;
  ariEndOfLifeServicesDates?: string;
  vsiName?: string;
  vsiAddress?: string;
  vsiCity?: string;
  vsiState?: string;
  vsiZip?: string;
  vsiSalesContactName?: string;
  vsiSalesContactEmail?: string;
  vsiSalesContactPhone?: string;
  vsiSalesContactCellPhone?: string;
  vsiLeadSupportName?: string;
  vsiLeadSupportEmail?: string;
  vsiLeadSupportPhone?: string;
  vsiLeadSupportCellPhone?: string;
}

export interface RoomCertification {
  certificationDate?: number;
  certifiedBy?: string;
  certificationDetails?: { [key: string]: string };
}

export interface RoomComment {
  producerNotes?: string;
}

export interface RoomStatus {
  stale?: boolean;
  loginError?: string;
  inCall?: boolean;
  reachable?: boolean;
  pauseMonitoringStatus?: PauseMonitoringStatus;
  apiError?: string;
  lastUpdated?: number;
  monitoringTriggersStatus?: MonitoringTriggersStatus;
}

export interface RoomSummary {
  hasVipDevices?: boolean;
  roomId?: string;
  totalDevices?: number;
  /**
   * totalLinkError
   */
  totalOffline: number;
  /**
   * totalLinkOK
   */
  totalOnline: number;
  totalLinkUnknown: number;
  totalRoomDevices: number;
  totalStale: number;
  totalLoginOK: number;
  totalLoginError: number;
  totalLoginUnknown: number;
  totalApiOK: number;
  totalApiError: number;
  totalApiUnknown: number;
}

export interface PauseMonitoringStatus extends PauseMonitoringOption {
  outOfScheduleUntil?: number;
  parentPaused?: boolean;
  outOfSchedule?: boolean;
  resumeOn?: number;
}

export interface MonitoringTriggersStatus {
  paused?: boolean;
  blocked?: boolean;
  since?: number;
  until?: number;
}

export interface PauseMonitoringOption {
  paused: boolean;
  comment?: string;
  pausedSince?: number;
  pausedUntil?: number;
}

export type RoomsCount = number;

export interface RoomsFilters {
  accountId?: string[];
  id?: string[];
  name?: string;
  managed?: string;
  vip?: boolean;
  ["details.publicRoom"]?: boolean;
  ["details.roomType"]?: string[];
  ["details.roomNumber"]?: string;
  ["details.contactName"]?: string;
  ["details.contactEmail"]?: string;
  ["details.certificationStatus"]?: boolean;
  ["details.certifiedBy"]?: string;
  ["details.serviceExpirationDate.after"]?: number;
  ["details.serviceExpirationDate.before"]?: number;
  ["device.type"]?: string[];
  inService?: boolean;
  ["location.country"]?: string[];
  ["location.id"]?: string;
  ["location.name"]?: string;
  ["location.latitude"]?: string;
  ["location.longitude"]?: string;
  ["location.radius"]?: number;
  ["status.inCall"]?: boolean;
  ["status.reachable"]?: boolean;
  ["status.stale"]?: boolean;
  ["status.apiError"]?: boolean;
  ["status.loginError"]?: boolean;
  ["status.pauseMonitoringStatus.paused"]?: boolean;
  ["status.monitoringTriggersStatus.paused"]?: boolean;
  ["location.open"]?: boolean;
  excludeSimpleAssets?: boolean;
  exchangeId?: string;
  tags?: string[];
}

export enum SchedulingOption {
  Unknown = "Unknown",
  None = "None",
  VCScheduling = "VC Scheduling",
  RoomOnlyScheduling = "Room Only Scheduling",
}
